/**
 * Link preview enrichment for outgoing messages.
 * Port of flock-expo/src/services/chatPreview.service.ts (verbatim logic).
 */
import { api } from '@/lib/apiClient';
import type { LinkPreview, MessageContent } from '@flock-types/chat.types';
import {
  buildOptimisticLinkPreview,
  extractFirstUrl,
  mergeLinkPreview,
  removeUndefinedLinkPreviewFields,
} from '@/lib/linkEmbeds';

const PREVIEW_FETCH_WAIT_MS = 1200;

interface LinkPreviewResponse {
  success: boolean;
  data: LinkPreview;
}

export async function buildLinkPreviewForText(text?: string): Promise<LinkPreview | undefined> {
  const url = extractFirstUrl(text);
  if (!url) return undefined;

  const optimistic = buildOptimisticLinkPreview(url);

  const enrichedPreview = api
    .post<LinkPreviewResponse>('/chat/link-preview', { url })
    .then((response) => mergeLinkPreview(optimistic, response.data))
    .catch((error) => {
      console.warn('[chatPreview] Preview fetch failed, sending fallback preview:', error);
      return optimistic;
    });

  const fallbackPreview = new Promise<LinkPreview>((resolve) => {
    setTimeout(() => resolve(optimistic), PREVIEW_FETCH_WAIT_MS);
  });

  return Promise.race([enrichedPreview, fallbackPreview]);
}

export async function attachLinkPreviewToContent(content: MessageContent): Promise<MessageContent> {
  const text = content.userText || content.text;
  const url = extractFirstUrl(text);

  if (url && content.linkPreview?.url === url) {
    return {
      ...content,
      linkPreview: removeUndefinedLinkPreviewFields(content.linkPreview),
    };
  }

  const preview = url ? await buildLinkPreviewForText(text) : undefined;
  if (!preview) {
    const { linkPreview: _ignored, ...withoutPreview } = content;
    return withoutPreview;
  }

  return {
    ...content,
    linkPreview: removeUndefinedLinkPreviewFields(preview),
  };
}
